import React, { MouseEvent, use, useEffect, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import moment, { Moment } from 'moment';
import {
    boolOperators,
    containsOperators,
    numericAndDateOperators,
    operatorList,
    stringOperators,
} from './grid-custom-filter.data';
import styles from './style.module.scss';
import {
    instanceOfGridSearch,
    instanceOfGridSearchExtendedClient,
} from '@components-clean/core/api-grid/api-grid.helper';
import Button from '@components-clean/core/button';
import { DatePickerController } from '@components-clean/core/date-picker';
import { DateTimePickerController } from '@components-clean/core/datetime-picker';
import { DropDownListController } from '@components-clean/core/drop-down-list';
import {
    ClientGridFilter,
    ClientGridFilterValues,
    DataType,
    FilterFormValues,
    FilterOperatorItem,
    FilterOperators,
    GridCustomFilterProps,
    GridFilter,
    GridSearch,
    RAGFilterChipValues,
    RAGFilterType,
    SelectValue,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import { applyDecimalOperator, applyOperatorLogic, isNumberDecimal } from '@components-clean/core/grid/grid.helper';
import { Popup } from '@components-clean/core/popup/popup';
import {
    LogicOperator,
    ValueLabel,
} from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import {
    booleanValues,
    directionValues,
    evaluationRagColorValues,
    groupOperators,
    ragColorValues,
} from '@components-clean/core/study-data-filter/advanced-select/advanced-select.utils';
import { PrimitiveValues } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';
import { LocalStateContext } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromUTC, javascriptDateWithTimeFromUTC } from '@utils/convertor';

function instanceOfDate(object: unknown): object is Date {
    return object !== null && typeof object === 'object' && object instanceof Date;
}

export const getComponentForDataType = (
    formControl: Control<FilterFormValues, unknown>,
    name: 'value1' | 'value2',
    value1Disabled: boolean,
    value2Disabled: boolean,
    t: (translate: string) => string,
    handleInputChange: (evt: GenericEvent) => void,
    type?: DataType,
    ignoreReadonlyContext?: boolean,
    lovData?: string[],
    ragColorUI?: boolean,
    evaluationColumn?: boolean,
    directionValueColumn?: boolean,
) => {
    if (lovData && lovData.length > 0) {
        return (
            <DropDownListController
                control={formControl}
                name={name}
                data-testid={`grid-filter-${name}`}
                data={lovData.map((x) => ({
                    value: x,
                    label: x,
                }))}
                textField="label"
                clearButton={false}
                fillMode="flat"
                dataItemKey="value"
                disabled={name === 'value1' ? value1Disabled : value2Disabled}
                handleInputChange={handleInputChange}
                ignoreReadonlyContext={ignoreReadonlyContext}
            />
        );
    }

    if (directionValueColumn) {
        return (
            <DropDownListController
                control={formControl}
                name={name}
                data-testid={`grid-filter-${name}`}
                data={directionValues.map((x) => ({
                    value: x.value,
                    label: t(x.label),
                }))}
                textField="label"
                clearButton={false}
                fillMode="flat"
                dataItemKey="value"
                disabled={name === 'value1' ? value1Disabled : value2Disabled}
                handleInputChange={handleInputChange}
                ignoreReadonlyContext={ignoreReadonlyContext}
            />
        );
    }

    if (ragColorUI) {
        return (
            <DropDownListController
                control={formControl}
                name={name}
                data-testid={`grid-filter-${name}`}
                data={
                    evaluationColumn
                        ? evaluationRagColorValues.map((x) => ({
                              value: x.value,
                              label: t(x.label),
                          }))
                        : ragColorValues.map((x) => ({
                              value: x.value,
                              label: t(x.label),
                          }))
                }
                textField="label"
                clearButton={false}
                fillMode="flat"
                dataItemKey="value"
                disabled={name === 'value1' ? value1Disabled : value2Disabled}
                handleInputChange={handleInputChange}
                ignoreReadonlyContext={ignoreReadonlyContext}
            />
        );
    }

    switch (type) {
        case DataType.Int:
        case DataType.Decimal:
            return (
                <NumericTextBoxController
                    control={formControl}
                    name={name}
                    data-testid={`grid-filter-${name}`}
                    fillMode="flat"
                    disabled={name === 'value1' ? value1Disabled : value2Disabled}
                    format="0.##"
                />
            );
        case DataType.Bool:
            return (
                <DropDownListController
                    control={formControl}
                    name={name}
                    data-testid={`grid-filter-${name}`}
                    data={booleanValues.map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }))}
                    textField="label"
                    clearButton={false}
                    fillMode="flat"
                    dataItemKey="value"
                    disabled={name === 'value1' ? value1Disabled : value2Disabled}
                    handleInputChange={handleInputChange}
                    ignoreReadonlyContext={ignoreReadonlyContext}
                />
            );
        case DataType.Date:
            return (
                <DatePickerController
                    control={formControl}
                    name={name}
                    data-testid={`grid-filter-${name}`}
                    fillMode="flat"
                    disabled={name === 'value1' ? value1Disabled : value2Disabled}
                    handleInputChange={handleInputChange}
                />
            );
        case DataType.DateTime:
            return (
                <DateTimePickerController
                    control={formControl}
                    name={name}
                    data-testid={`advanced-select-${name}`}
                    fillMode="flat"
                    disabled={name === 'value1' ? value1Disabled : value2Disabled}
                />
            );
        case DataType.String:
        default:
            return (
                <TextBoxController
                    control={formControl}
                    name={name}
                    data-testid={`grid-filter-${name}`}
                    fillMode="flat"
                    disabled={name === 'value1' ? value1Disabled : value2Disabled}
                />
            );
    }
};

export type GenericEvent = DatePickerChangeEvent | ComboBoxChangeEvent | TextBoxChangeEvent;

export const FilterUi = (
    control: Control<FilterFormValues, unknown>,
    operators: FilterOperatorItem[],
    handleInputChange: (
        field: 'value1' | 'value2' | 'operator1' | 'operator2' | 'groupOperator',
    ) => (event: GenericEvent) => void,
    value1Disabled: boolean,
    value2Disabled: boolean,
    dataType: DataType,
    ignoreReadonlyContext: boolean = false,
    lovData?: string[],
    ragColorUI?: boolean,
    ragFilterType?: RAGFilterType,
    evaluationColumn?: boolean,
) => {
    const { t } = useTranslation();

    return ragFilterType === RAGFilterType.Direction && ragColorUI ? (
        <div className={styles.ragFilterDirection}>
            <div>
                <span>{t('from')}</span>
                {getComponentForDataType(
                    control,
                    'value1',
                    value1Disabled,
                    value2Disabled,
                    t,
                    handleInputChange('value1'),
                    dataType,
                    ignoreReadonlyContext,
                    lovData,
                    true,
                )}
            </div>
            <div>
                <span>{t('to')}</span>
                {getComponentForDataType(
                    control,
                    'value2',
                    value1Disabled,
                    value2Disabled,
                    t,
                    handleInputChange('value2'),
                    dataType,
                    ignoreReadonlyContext,
                    lovData,
                    true,
                )}
            </div>
        </div>
    ) : (
        <>
            <DropDownListController
                control={control}
                name="operator1"
                data-testid="grid-filter-operator1"
                data={operators}
                handleInputChange={handleInputChange('operator1')}
                textField="label"
                clearButton={false}
                fillMode="flat"
                ignoreReadonlyContext={ignoreReadonlyContext}
            />
            {getComponentForDataType(
                control,
                'value1',
                value1Disabled,
                value2Disabled,
                t,
                handleInputChange('value1'),
                dataType,
                ignoreReadonlyContext,
                lovData,
                ragColorUI,
                evaluationColumn,
                ragFilterType === RAGFilterType.Direction,
            )}
            <DropDownListController
                control={control}
                name="groupOperator"
                data-testid="grid-filter-groupOperator"
                data={groupOperators.map((x) => ({
                    value: x.value,
                    label: t(x.label),
                }))}
                handleInputChange={handleInputChange('groupOperator')}
                textField="label"
                clearButton={false}
                dataItemKey="value"
                fillMode="flat"
                ignoreReadonlyContext={ignoreReadonlyContext}
            />
            <DropDownListController
                control={control}
                name="operator2"
                data-testid="grid-filter-operator2"
                data={operators}
                handleInputChange={handleInputChange('operator2')}
                textField="label"
                clearButton={false}
                fillMode="flat"
                ignoreReadonlyContext={ignoreReadonlyContext}
            />
            {getComponentForDataType(
                control,
                'value2',
                value1Disabled,
                value2Disabled,
                t,
                handleInputChange('value2'),
                dataType,
                ignoreReadonlyContext,
                lovData,
                ragColorUI,
                evaluationColumn,
                ragFilterType === RAGFilterType.Direction,
            )}
        </>
    );
};

const GridCustomFilter = ({
    anchor,
    show,
    filterValue,
    dataType,
    anchorAlign,
    popupAlign,
    onCancel,
    onSelect,
    clientGrid,
    lovData,
    lovFilterContains,
    filterKey,
    ragColumn,
    singleValueRagColumn = false,
    searchText,
    multiValueTypeColumn,
    evaluationColumn,
}: GridCustomFilterProps) => {
    const [value1Disabled, setValue1Disabled] = useState<boolean>(false);
    const [value2Disabled, setValue2Disabled] = useState<boolean>(false);
    const { t } = useTranslation();
    const [chip, setChip] = useState<RAGFilterChipValues>(RAGFilterChipValues.Value);
    const [ragFilterType, setRagFilterType] = useState<RAGFilterType>(
        !clientGrid && multiValueTypeColumn ? RAGFilterType.Current : RAGFilterType.DatasetInFocus,
    );
    const { state: localState } = use(LocalStateContext);

    const getOperatorsForDataType = () => {
        if (lovFilterContains)
            return operatorList
                .filter((x) => containsOperators.includes(x.value))
                .map((x) => ({
                    value: x.value,
                    label: t(x.label),
                }));

        if (lovData && lovData.length > 0)
            return operatorList
                .filter((x) => boolOperators.includes(x.value))
                .map((x) => ({
                    value: x.value,
                    label: t(x.label),
                }));

        switch (dataType) {
            case DataType.Bool:
                return operatorList
                    .filter((x) => boolOperators.includes(x.value))
                    .map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }));
            case DataType.String:
                return operatorList
                    .filter((x) => stringOperators.includes(x.value))
                    .map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }));
            case DataType.Int:
            case DataType.Decimal:
            case DataType.Date:
            default:
                return operatorList
                    .filter((x) => numericAndDateOperators.includes(x.value))
                    .map((x) => ({
                        value: x.value,
                        label: t(x.label),
                    }));
        }
    };

    const getOperatorsForRagColors = () => {
        return operatorList
            .filter((x) => boolOperators.includes(x.value))
            .map((x) => ({
                value: x.value,
                label: t(x.label),
            }));
    };

    const operators = getOperatorsForDataType();
    const ragColorOperators = getOperatorsForRagColors();
    const defaultOperator =
        dataType === DataType.String && !lovData
            ? operators.find((x) => x.value === FilterOperators.Contains)
            : operators[0];
    const { control, handleSubmit, resetField, reset, setValue, watch } = useForm<FilterFormValues>({
        defaultValues: {
            operator1: defaultOperator,
            value1: dataType === DataType.String ? '' : null,
            operator2: defaultOperator,
            value2: dataType === DataType.String ? '' : null,
            groupOperator: { value: groupOperators[0].value, label: t(groupOperators[0].label) },
        },
    });

    const [val1, val2, operator1, operator2] = watch(['value1', 'value2', 'operator1', 'operator2']);

    const handleOnClear = (e: MouseEvent<HTMLButtonElement>) => {
        reset();
        onCancel(e);
    };

    const mutateCriteria = (
        operator: FilterOperatorItem,
        value: PrimitiveValues | ValueLabel<boolean | null> | ValueLabel<string | null>,
        criteriaPos: number,
    ): SelectValue => {
        let val = null;

        const multiValueProps: {
            multiValueType?: RAGFilterType;
        } = {
            multiValueType:
                ragFilterType === RAGFilterType.Direction && chip === RAGFilterChipValues.Color
                    ? criteriaPos === 1
                        ? RAGFilterType.Previous
                        : RAGFilterType.Current
                    : undefined,
        };

        // Handle filter forms that use ValueLabel objects (LOV/Dropdowns/Booleans etc.)
        if (value !== undefined && value !== null && typeof value === 'object' && 'value' in value) {
            if (value.value != null) val = value.value.toString();
            return {
                operator: operator.value,
                value: val,
                dataType,
                ...multiValueProps,
            };
        }
        switch (dataType) {
            case DataType.Bool:
                // This should be handled by the ValueLabel case above
                throw new Error('Error filtering on boolean values');
            case DataType.Date:
                if (value != null) val = moment(value as Date);

                return {
                    operator: operator.value,
                    value: val?.format('YYYY-MM-DD') ?? null,
                    dataType,
                    ...multiValueProps,
                };
            case DataType.DateTime:
                if (value != null) val = moment(value as Date);

                return {
                    operator: operator.value,
                    value: val?.format('YYYY-MM-DD[T]HH:mm:ss[Z]') ?? null,
                    dataType,
                    ...multiValueProps,
                };
            case DataType.Int:
                if (value != null) val = value as number;

                return {
                    operator: operator.value,
                    value: String(val),
                    dataType,
                    ...multiValueProps,
                };
            case DataType.Decimal:
                if (value != null) val = value as number;

                return {
                    operator: operator.value,
                    value: String(val),
                    dataType,
                    ...multiValueProps,
                };
            case DataType.String:
            default:
                if (value != null) val = value as string;
                return {
                    operator: operator.value,
                    value: String(val),
                    dataType,
                    ...multiValueProps,
                };
        }
    };

    const onSubmit = (data: FilterFormValues, e: MouseEvent<HTMLButtonElement>) => {
        const filter: GridFilter = {
            logic: data.groupOperator.value,
            filters: [],
            multiValueType: multiValueTypeColumn ? ragFilterType : undefined,
            ragType: ragColumn || multiValueTypeColumn ? chip : undefined,
        };

        switch (data.operator1.value) {
            case FilterOperators.IsNull:
            case FilterOperators.IsNotNull:
                filter.filters.push(mutateCriteria(data.operator1, null, 1));
                break;
            default:
                if (data.value1 != null && data.value1 !== '') {
                    filter.filters.push(mutateCriteria(data.operator1, data.value1, 1));
                }
        }

        switch (data.operator2.value) {
            case FilterOperators.IsNull:
            case FilterOperators.IsNotNull:
                filter.filters.push(mutateCriteria(data.operator2, null, 2));
                break;
            default:
                if (data.value2 != null && data.value2 !== '') {
                    filter.filters.push(mutateCriteria(data.operator2, data.value2, 2));
                }
        }

        onSelect(filter, e);
    };

    const mutateClientCriteria = (
        value: PrimitiveValues | ValueLabel<boolean | null> | ValueLabel<string | null>,
    ): string | Date | Moment | boolean | null => {
        let val = null;

        if (
            (lovData && lovData.length > 0) ||
            chip === RAGFilterChipValues.Color ||
            ragFilterType === RAGFilterType.Direction
        ) {
            if (value != null) val = (value as ValueLabel<string | null>).value;
            return val;
        }
        switch (dataType) {
            case DataType.Bool:
                if (value != null) val = (value as ValueLabel<boolean | null>).value;
                return val;
            case DataType.Date:
                if (value != null) val = moment(value as Date);
                return val ?? null;
            case DataType.DateTime:
                if (value != null) val = moment(value as Date);
                return val ?? null;
            case DataType.Int:
                if (value != null) val = value as number;
                return val != null ? String(val) : null;
            case DataType.Decimal:
                if (value != null) val = value as number;
                return val != null ? String(val) : null;
            case DataType.String:
            default:
                if (value != null) val = value as string;
                return val;
        }
    };

    const applyOperator = (
        current: string | boolean | null | undefined,
        value: string | Date | boolean | null | undefined,
        operator: FilterOperatorItem,
    ) => {
        if (value === undefined) return true;

        if (chip === RAGFilterChipValues.Color) {
            if (operator.value === FilterOperators.IsNull || operator.value === FilterOperators.IsNotNull) {
                const rawColour =
                    current && typeof current === 'object' && 'colour' in current
                        ? (current as { colour?: string | number | null }).colour
                        : current;

                const colourValue = rawColour == null ? null : String(rawColour).toUpperCase();
                const isBlank =
                    colourValue == null || colourValue === '' || colourValue === 'NONE' || colourValue === '0';

                return operator.value === FilterOperators.IsNull ? isBlank : !isBlank;
            }

            if (
                value === '0' &&
                (operator.value === FilterOperators.Equal || operator.value === FilterOperators.NotEqual)
            ) {
                const rawColour =
                    current && typeof current === 'object' && 'colour' in current
                        ? (current as { colour?: string | number | null }).colour
                        : current;

                const isNone = rawColour == null || rawColour === '';

                if (operator.value === FilterOperators.Equal && isNone) return true;
                if (operator.value === FilterOperators.NotEqual && isNone) return false;
            }
        }

        if (current && typeof current === 'object' && 'colour' in current && chip === RAGFilterChipValues.Color) {
            current = (current as { colour: string | number | null }).colour?.toString().toUpperCase();
            if (current != null) {
                const index = HC_RAG_NAMES.map((name) => name.toUpperCase()).indexOf(current);
                current = index > -1 ? index.toString() : current;
            }
        } else if (
            current &&
            typeof current === 'object' &&
            'colourInFocus' in current &&
            chip === RAGFilterChipValues.Color
        ) {
            const comparisonValue = 'comparisonColour';
            if (ragFilterType === RAGFilterType.DatasetInFocus)
                current = (current as { colourInFocus: string }).colourInFocus;
            else current = current[comparisonValue];
        } else if (current && filterKey && typeof current === 'object') {
            const comparisonValue = 'comparisonValue';
            if (ragFilterType === RAGFilterType.DatasetInFocus) current = current[filterKey];
            else current = current[comparisonValue];

            if (dataType === DataType.Int && value != null && isNumberDecimal(current)) {
                const float = Number.parseFloat(value.toString());
                if (!isNaN(float)) return applyDecimalOperator(operator, current, float);
            }
        }

        return applyOperatorLogic(current, value, operator, dataType);
    };

    const applyDirectionOperator = (
        current: string | boolean | null | undefined,
        value: string | Date | boolean | null | undefined,
        key: string,
        operator: FilterOperatorItem,
    ) => {
        if (value === undefined) return true;

        if (current && typeof current === 'object' && key in current) {
            current = current[key];
        }
        return applyOperatorLogic(current, value, operator);
    };

    const calcOperator = (op1: FilterOperatorItem, op2: FilterOperatorItem, value: LogicOperator) => {
        return (current: string, { value1, value2 }: { value1: string; value2: string }) =>
            value === LogicOperator.AND
                ? applyOperator(current, value1, op1) && applyOperator(current, value2, op2)
                : (value1 !== undefined && applyOperator(current, value1, op1)) ||
                  (value2 !== undefined && applyOperator(current, value2, op2));
    };

    const calcDirectionOperator = (
        op1: FilterOperatorItem,
        op2: FilterOperatorItem,
        ragType: RAGFilterChipValues,
        groupOperator: LogicOperator,
    ) => {
        return (current: string, { value1, value2 }: { value1: string; value2: string }) => {
            const result1 = applyDirectionOperator(
                current,
                value1,
                ragType === RAGFilterChipValues.Color ? 'comparisonColour' : 'changeDirection',
                op1,
            );
            const result2 = applyDirectionOperator(
                current,
                value2,
                ragType === RAGFilterChipValues.Color ? 'colourInFocus' : 'changeDirection',
                op2,
            );

            if (groupOperator === LogicOperator.AND) return result1 && result2;

            return result1 || result2;
        };
    };

    const onClientSubmit = (data: FilterFormValues, e: MouseEvent<HTMLButtonElement>) => {
        let filter1;
        let filter2;
        switch (data.operator1.value) {
            case FilterOperators.IsNull:
            case FilterOperators.IsNotNull:
                filter1 = mutateClientCriteria(null);
                break;
            default:
                if (data.value1 != null && data.value1 !== '') {
                    filter1 = mutateClientCriteria(data.value1);
                }
        }

        switch (data.operator2.value) {
            case FilterOperators.IsNull:
            case FilterOperators.IsNotNull:
                filter2 = mutateClientCriteria(null);
                break;
            default:
                if (data.value2 != null && data.value2 !== '') {
                    filter2 = mutateClientCriteria(data.value2);
                }
        }
        const result: ClientGridFilter = {
            filterValues: {
                value1: filter1,
                value2: filter2,
            },
            operator:
                ragFilterType === RAGFilterType.Direction
                    ? calcDirectionOperator(data.operator1, data.operator2, chip, data.groupOperator.value)
                    : calcOperator(data.operator1, data.operator2, data.groupOperator.value),
        };
        onSelect(result, e);
    };

    const handleOperatorChange = (name: 'operator1' | 'operator2', value: FilterOperatorItem) => {
        let disableValue = false;
        if (value != null && (value.value === FilterOperators.IsNull || value.value === FilterOperators.IsNotNull)) {
            resetField(name === 'operator1' ? 'value1' : 'value2');
            disableValue = true;
        }

        if (name === 'operator1') setValue1Disabled(disableValue);
        else setValue2Disabled(disableValue);
    };

    const updateFilterUsingSearch = (search: GridSearch) => {
        let operatorIdx: number;
        let value: FilterFormValues['value1'] = search.search;

        if (lovData && lovData.length > 0) {
            operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
            value = {
                value,
                label: value,
            };
        } else
            switch (search.type) {
                case DataType.Int:
                case DataType.Decimal:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    value = Number(value);
                    break;
                case DataType.Bool:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    const boolValueIdx = booleanValues.findIndex((item) => String(item.value) === value);
                    if (boolValueIdx === -1) return;
                    value = {
                        value: booleanValues[boolValueIdx].value,
                        label: t(booleanValues[boolValueIdx].label),
                    };
                    break;
                case DataType.Date:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    value = javascriptDateWithoutTimeFromUTC(value);
                    break;
                case DataType.DateTime:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    value = javascriptDateWithTimeFromUTC(value);
                    break;
                case DataType.String:
                default:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Contains);
                    break;
            }

        setValue('value1', value);
        setValue('operator1', operators[operatorIdx]);
    };

    const updateClientFilterUsingSearch = (search: string | boolean | number | ClientGridFilterValues | GridSearch) => {
        /*
         *  This function does far more than you think it should. It no longer just maps from the "search" input to the
         *  advanced filter popup. It also handles other filter defaulting and other weird filter stuff (I haven't identified it all)
         *  Be wary of changing anything in this function, as here be dragons.
         * */
        let operatorIdx: number;
        let operator2Idx: number = 0;
        let value: FilterFormValues['value1'] | ClientGridFilterValues | Date | Moment | GridSearch = search;
        let value2: FilterFormValues['value2'] | ClientGridFilterValues | Date | Moment | GridSearch = search;
        let operatorFromSearch: FilterOperators | undefined;
        let operator2FromSearch: FilterOperators | undefined;

        if (value !== undefined && value !== null && typeof value === 'object' && 'value1Operator' in value) {
            if (value.value1Operator != null) {
                operatorFromSearch = value.value1Operator as FilterOperators;
            }
        }

        if (value2 !== undefined && value2 !== null && typeof value2 === 'object' && 'value2Operator' in value2) {
            if (value2.value2Operator != null) {
                operator2FromSearch = value2.value2Operator as FilterOperators;
            }
        }

        if (value !== undefined && value !== null && typeof value === 'object' && 'value1' in value) {
            if (value.value1 != null) {
                value = value.value1 as PrimitiveValues | ValueLabel<boolean | null> | ValueLabel<string | null>;
            }
        }

        if (value2 !== undefined && value2 !== null && typeof value2 === 'object' && 'value2' in value2) {
            if (value2.value2 != null) {
                value2 = value2.value2 as PrimitiveValues | ValueLabel<boolean | null> | ValueLabel<string | null>;
            }
        } else {
            value2 = null;
        }

        if (value != null && instanceOfGridSearch(value)) value = value.search;
        if (value2 != null && instanceOfGridSearch(value2)) value2 = value2.search;

        if (lovData && lovData.length > 0) {
            const operator = operatorFromSearch !== undefined ? operatorFromSearch : FilterOperators.Equal;
            operatorIdx = operators.findIndex((item) => item.value === operator);

            const op2 = operator2FromSearch !== undefined ? operator2FromSearch : FilterOperators.Equal;
            operator2Idx = operators.findIndex((item) => item.value === op2);
            if (typeof value === 'string') {
                value = {
                    value: String(value),
                    label: String(value),
                };
            }

            if (typeof value2 === 'string') {
                value2 = {
                    value: String(value2),
                    label: String(value2),
                };
            }
        } else if (ragFilterType === RAGFilterType.Direction && chip === RAGFilterChipValues.Value) {
            operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
            const directionValueIndex = directionValues.findIndex((item) => item.value === value);
            if (directionValueIndex === -1) return;
            value = {
                value: directionValues[directionValueIndex].value,
                label: t(directionValues[directionValueIndex].label),
            };
        } else if (chip === RAGFilterChipValues.Color) {
            operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
            if (evaluationColumn) {
                const evaluationRagColorValueIdx = evaluationRagColorValues.findIndex((item) => item.value === value);
                if (evaluationRagColorValueIdx === -1) return;
                value = {
                    value: evaluationRagColorValues[evaluationRagColorValueIdx].value,
                    label: t(evaluationRagColorValues[evaluationRagColorValueIdx].label),
                };
            } else {
                const ragColorValueIdx = ragColorValues.findIndex((item) => item.value === value);
                if (ragColorValueIdx === -1) return;
                value = {
                    value: ragColorValues[ragColorValueIdx].value,
                    label: t(ragColorValues[ragColorValueIdx].label),
                };
            }
        } else if (operator1.value === FilterOperators.IsNull || operator1.value === FilterOperators.IsNotNull) {
            return;
        } else
            switch (dataType) {
                case DataType.Int:
                case DataType.Decimal:
                    const operator = operatorFromSearch !== undefined ? operatorFromSearch : FilterOperators.Equal;
                    operatorIdx = operators.findIndex((item) => item.value === operator);
                    if (operatorIdx === 2 || operatorIdx === 3) {
                        value = null;
                    } else {
                        value = Number(value);
                    }
                    break;
                case DataType.Bool:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    const boolValueIdx = booleanValues.findIndex((item) => item.value === value);
                    if (boolValueIdx === -1) return;
                    value = {
                        value: booleanValues[boolValueIdx].value,
                        label: t(booleanValues[boolValueIdx].label),
                    };
                    break;
                case DataType.Date:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    value = moment.isMoment(value)
                        ? value?.toDate()
                        : javascriptDateWithoutTimeFromUTC(value as string);
                    break;
                case DataType.DateTime:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Equal);
                    value = javascriptDateWithTimeFromUTC(value as string);
                    break;
                case DataType.String:
                default:
                    operatorIdx = operators.findIndex((item) => item.value === FilterOperators.Contains);
                    break;
            }

        setValue('value1', value as PrimitiveValues | ValueLabel<boolean | null> | ValueLabel<string | null>);
        if (operator1 !== null && !operator1.value) setValue('operator1', operators[operatorIdx]);

        if (operator2FromSearch) {
            setValue('value2', value2 as PrimitiveValues | ValueLabel<boolean | null> | ValueLabel<string | null>);
            if (operator2 !== null && !operator2.value) setValue('operator2', operators[operator2Idx]);
        }
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setChip(RAGFilterChipValues.Value);
    }, [searchText]);

    // NOTE: Reset the form when the Clear Filter button is clicked
    useEffect(() => {
        if (filterValue === null || filterValue === '') {
            reset();
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setValue1Disabled(false);
            setValue2Disabled(false);
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [filterValue]);

    useEffect(() => {
        if (clientGrid) {
            if (filterValue != null && instanceOfGridSearchExtendedClient(filterValue)) {
                updateClientFilterUsingSearch(filterValue);
            }
        } else if (filterValue && instanceOfGridSearch(filterValue)) {
            updateFilterUsingSearch(filterValue);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [show]);

    const ui = FilterUi(
        control,
        chip === RAGFilterChipValues.Color || ragFilterType === RAGFilterType.Direction ? ragColorOperators : operators,
        (field) => (event) => {
            if (field === 'operator1' || field === 'operator2')
                handleOperatorChange(field, event.target.value as FilterOperatorItem);
        },
        value1Disabled,
        value2Disabled,
        dataType,
        false,
        lovData,
        chip === RAGFilterChipValues.Color,
        ragFilterType,
        evaluationColumn,
    );

    const isBtnDisabled = () => {
        let isDisabled = false;
        const isOperator1Nullable =
            operator1 !== null &&
            (operator1.value === FilterOperators.IsNull || operator1.value === FilterOperators.IsNotNull);
        const isOperator2Nullable =
            operator2 !== null &&
            (operator2.value === FilterOperators.IsNull || operator2.value === FilterOperators.IsNotNull);

        if (val1 == null && !isOperator1Nullable && val2 == null && !isOperator2Nullable) {
            isDisabled = true;
        } else if (
            dataType === DataType.String &&
            val1 === '' &&
            !isOperator1Nullable &&
            val2 === '' &&
            !isOperator2Nullable
        )
            isDisabled = true;

        if (
            (dataType === DataType.Date || dataType === DataType.DateTime) &&
            ((instanceOfDate(val1) && val1?.getFullYear() < 1000) ||
                (instanceOfDate(val2) && val2?.getFullYear() < 1000))
        ) {
            isDisabled = true;
        }

        // This check is for LOV type. val1 is an empty object as default so we need to check the value for it.

        if (
            (val1 == null || (typeof val1 === 'object' && 'value' in val1 && val1.value === '')) &&
            !isOperator1Nullable &&
            (val2 === '' || val2 == null) &&
            !isOperator2Nullable
        ) {
            isDisabled = true;
        }

        if (ragFilterType === RAGFilterType.Direction && chip === RAGFilterChipValues.Color && (!val1 || !val2))
            isDisabled = true;

        return isDisabled;
    };

    return (
        <Popup
            data-testid="grid-filter-menu"
            anchor={anchor}
            show={show}
            anchorAlign={anchorAlign}
            popupAlign={popupAlign}
            onBlur={() => onCancel(undefined, false)}
            popupClass="k-rounded-md"
            collision={{ vertical: 'flip', horizontal: 'fit' }}
        >
            <form>
                <div className={cn(styles.formContainer, 'k-d-flex k-flex-col')}>
                    <div className={cn(styles.header, 'k-d-flex k-align-items-center')}>
                        <span className="k-icon k-font-icon k-i-filter" />
                        <div>{t('filter')}</div>
                    </div>
                    {clientGrid &&
                        !singleValueRagColumn &&
                        multiValueTypeColumn &&
                        localState?.selectedComparisonCM2Dataset?.dataCutDate && (
                            <div className={cn(styles.body)}>
                                <div className={cn('k-d-flex', styles.ragFilterType)}>
                                    <Button
                                        data-testid="grid-rag-filter-type-comparison"
                                        type="button"
                                        disabled={filterValue != null}
                                        themeColor="primary"
                                        className={
                                            ragFilterType === RAGFilterType.ComparisonDataset ? styles.active : ''
                                        }
                                        onClick={() => {
                                            if (ragFilterType !== RAGFilterType.ComparisonDataset) {
                                                if (ragFilterType === RAGFilterType.Direction) reset();
                                                setRagFilterType(RAGFilterType.ComparisonDataset);
                                            }
                                        }}
                                    >
                                        {localState?.selectedComparisonCM2Dataset?.dataCutDate}
                                    </Button>
                                    <Button
                                        data-testid="grid-rag-filter-type-infocus"
                                        type="button"
                                        disabled={filterValue != null}
                                        themeColor="primary"
                                        className={ragFilterType === RAGFilterType.DatasetInFocus ? styles.active : ''}
                                        onClick={() => {
                                            if (ragFilterType !== RAGFilterType.DatasetInFocus) {
                                                if (ragFilterType === RAGFilterType.Direction) reset();
                                                setRagFilterType(RAGFilterType.DatasetInFocus);
                                            }
                                        }}
                                    >
                                        {localState?.selectedCM2Dataset?.dataCutDate}
                                    </Button>
                                    <Button
                                        data-testid="grid-rag-filter-type-direction"
                                        type="button"
                                        disabled={filterValue != null}
                                        themeColor="primary"
                                        className={ragFilterType === RAGFilterType.Direction ? styles.active : ''}
                                        onClick={() => {
                                            if (ragFilterType !== RAGFilterType.Direction) {
                                                reset();
                                                setRagFilterType(RAGFilterType.Direction);
                                                if (ragColumn) setChip(RAGFilterChipValues.Color);
                                            }
                                        }}
                                    >
                                        {t('direction')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    {!clientGrid && multiValueTypeColumn && (
                        <div className={cn(styles.body)}>
                            <div className={cn('k-d-flex', styles.ragFilterType)}>
                                <Button
                                    data-testid="grid-rag-filter-type-previous"
                                    type="button"
                                    disabled={filterValue != null}
                                    themeColor="primary"
                                    className={ragFilterType === RAGFilterType.Previous ? styles.active : ''}
                                    onClick={() => {
                                        if (ragFilterType !== RAGFilterType.Previous) {
                                            if (ragFilterType === RAGFilterType.Direction) reset();
                                            setRagFilterType(RAGFilterType.Previous);
                                        }
                                    }}
                                >
                                    {t('previous')}
                                </Button>
                                <Button
                                    data-testid="grid-rag-filter-type-current"
                                    type="button"
                                    disabled={filterValue != null}
                                    themeColor="primary"
                                    className={ragFilterType === RAGFilterType.Current ? styles.active : ''}
                                    onClick={() => {
                                        if (ragFilterType !== RAGFilterType.Current) {
                                            if (ragFilterType === RAGFilterType.Direction) reset();
                                            setRagFilterType(RAGFilterType.Current);
                                        }
                                    }}
                                >
                                    {t('current')}
                                </Button>
                                <Button
                                    data-testid="grid-rag-filter-type-direction"
                                    type="button"
                                    disabled={filterValue != null}
                                    themeColor="primary"
                                    className={ragFilterType === RAGFilterType.Direction ? styles.active : ''}
                                    onClick={() => {
                                        if (ragFilterType !== RAGFilterType.Direction) {
                                            reset();
                                            setRagFilterType(RAGFilterType.Direction);

                                            if (ragColumn) setChip(RAGFilterChipValues.Color);
                                        }
                                    }}
                                >
                                    {t('direction')}
                                </Button>
                            </div>
                        </div>
                    )}
                    {ragColumn && (
                        <div className={cn(styles.body, 'k-d-flex')}>
                            <Button
                                data-testid="grid-filter-value-type"
                                type="button"
                                themeColor={!multiValueTypeColumn ? 'primary' : 'secondary'}
                                disabled={filterValue != null}
                                className={cn(
                                    !multiValueTypeColumn
                                        ? chip === RAGFilterChipValues.Value
                                            ? styles.filterTypeButtonPrimaryActive
                                            : styles.filterTypeButtonPrimary
                                        : chip === RAGFilterChipValues.Value
                                          ? styles.filterTypeButtonActive
                                          : styles.filterTypeButton,
                                )}
                                onClick={() => {
                                    if (chip !== RAGFilterChipValues.Value) {
                                        reset();
                                        setChip(RAGFilterChipValues.Value);
                                    }
                                }}
                            >
                                {t('common:value')}
                            </Button>
                            <Button
                                data-testid="grid-filter-color-type"
                                type="button"
                                themeColor={!multiValueTypeColumn ? 'primary' : 'secondary'}
                                disabled={filterValue != null}
                                className={cn(
                                    !multiValueTypeColumn
                                        ? chip === RAGFilterChipValues.Color
                                            ? styles.filterTypeButtonPrimaryActive
                                            : styles.filterTypeButtonPrimary
                                        : chip === RAGFilterChipValues.Color
                                          ? styles.filterTypeButtonActive
                                          : styles.filterTypeButton,
                                )}
                                onClick={() => {
                                    if (chip !== RAGFilterChipValues.Color) {
                                        reset();
                                        setChip(RAGFilterChipValues.Color);
                                    }
                                }}
                            >
                                {t('common:color')}
                            </Button>
                        </div>
                    )}
                    <div className={cn(styles.body, 'k-d-flex k-flex-col')}>{ui}</div>
                    <div className={cn(styles.footer, 'k-d-flex')}>
                        <Button
                            data-testid="grid-filter-cancel"
                            type="button"
                            onClick={handleOnClear}
                            disabled={!filterValue}
                        >
                            {t('clear')}
                        </Button>
                        <Button
                            data-testid="grid-filter-confirm"
                            type="button"
                            primary
                            onClick={(e) =>
                                handleSubmit((data) => (clientGrid ? onClientSubmit(data, e) : onSubmit(data, e)))()
                            }
                            disabled={isBtnDisabled()}
                        >
                            {t('apply')}
                        </Button>
                    </div>
                </div>
            </form>
        </Popup>
    );
};

export default GridCustomFilter;
